import type { ResourceContext } from '@onefootprint/request-types/percy';
import { create } from 'zustand';
import type { ChatMessage } from '../../hooks/use-chat-ws';
import { resourceGroup, resourceKey, toResourceList } from '../../utils/resource-presentation';

// A resource the chat created or referenced, surfaced in the Context Explorer panel. `key` is the
// stable id (resourceKey), used to dedup the list and key each row.
export type ChatResource = {
  key: string;
  resource: ResourceContext;
};

// The chat's resources, in creation order, for the Context Explorer panel.
export type ChatResources = {
  items: ChatResource[];
};
const EMPTY_RESOURCES: ChatResources = {
  items: []
};

// Bridges the chat's resource list (derived inside ChatSession from its message stream) to the
// Context Explorer panel, which renders as a sibling of the chat in the widget. There's one global
// widget, so this is app-global state held in a module store: ChatSession publishes via
// setResources; the panel reads. ChatSession clears it on unmount so a different session/route
// doesn't keep showing the prior chat's resources.
type ChatResourcesStore = {
  resources: ChatResources;
  setResources: (resources: ChatResources) => void;
};
const useStore = create<ChatResourcesStore>(set => ({
  resources: EMPTY_RESOURCES,
  setResources: resources => set({
    resources
  })
}));
export const useChatResources = () => {
  return useStore(_temp);
};

/** For the chat session to publish its resources. */
export const useSetChatResources = () => {
  return useStore(_temp2);
};

// Derive the chat's resources from its message stream (history + live both arrive as
// `type: 'resource'` messages, plus the user's initial `context`) in a single pass. Dedup by
// resourceKey keeping the LAST occurrence's data so a later update's name wins; the Map preserves
// first-seen order, so the panel lists resources in the order they were created.
export const deriveChatResources = (messages: ChatMessage[]): ChatResources => {
  const byKey = new Map<string, ResourceContext>();
  const addResource = (resource: ResourceContext) => {
    // Only surface kinds the explorer groups; resourceGroup === null ⇒ not shown (ui_block /
    // agent_suggestion render their own UI).
    if (resourceGroup(resource) === null) return;
    byKey.set(resourceKey(resource), resource);
  };
  for (const message of messages) {
    // An older Modal backend may omit the nested `resource` on a resource frame — skip it.
    if (message.type === 'resource' && message.resource) {
      addResource(message.resource);
    }
    if (message.type === 'user') {
      for (const resource of toResourceList(message.context)) {
        addResource(resource);
      }
    }
  }
  return {
    items: [...byKey.entries()].map(([key, resource]) => ({
      key,
      resource
    }))
  };
};
function _temp(state) {
  return state.resources;
}
function _temp2(state) {
  return state.setResources;
}